import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { BadgeCheck, X } from 'lucide-react'

const panelToneClassNames = {
  current: {
    panel: 'border-info/20 bg-card/95 dark:border-info/20 dark:bg-background/20',
    header: 'border-b border-status-info-border bg-status-info-background',
    body: 'bg-background/65 dark:bg-background/15',
    content: 'text-muted-foreground dark:text-status-info-foreground',
  },
  next: {
    panel: 'border-warning/20 bg-card/95 dark:border-warning/20 dark:bg-background/20',
    header: 'border-b border-status-warning-border bg-status-warning-background',
    body: 'bg-background/65 dark:bg-background/15',
    content: 'text-muted-foreground dark:text-status-warning-foreground',
  },
  neutral: {
    panel: 'border-border/70 bg-card/95 dark:border-white/10 dark:bg-background/20',
    header: 'border-b border-border/60 bg-muted/35 dark:border-white/10 dark:bg-white/[0.04]',
    body: 'bg-background/65 dark:bg-background/15',
    content: 'text-muted-foreground',
  },
}

const snapshotToneClassNames = {
  pricing: 'border-border/70 bg-card dark:border-info/15 dark:bg-card/95',
  warning: 'border-warning/20 bg-card dark:border-warning/20 dark:bg-card/95',
  neutral: 'border-border/70 bg-card dark:border-white/10 dark:bg-card/95',
}

function PriceChangeSnapshotPanel({ title, tone = 'neutral', bodyAction = null, children }) {
  const panelTone = panelToneClassNames[tone] ?? panelToneClassNames.neutral

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border shadow-xs',
        panelTone.panel
      )}
    >
      <div className={cn('flex items-center justify-between gap-3 px-4 py-2.5', panelTone.header)}>
        <Typography variant="label" weight="medium" className="block">
          {title}
        </Typography>
      </div>
      <div
        className={cn(
          'flex flex-1 flex-col px-4 py-4',
          bodyAction && 'min-h-[96px]',
          panelTone.body
        )}
      >
        <div className={cn('text-sm leading-6', panelTone.content)}>{children}</div>
        {bodyAction ? <div className="mt-auto flex justify-end pt-3">{bodyAction}</div> : null}
      </div>
    </div>
  )
}

export function PriceChangeSnapshot({
  title = 'Price Change Snapshot',
  action = null,
  currentBody,
  currentAction = null,
  nextBody,
  currentTone = 'neutral',
  nextTone = 'neutral',
  tone = 'pricing',
  onDismiss = null,
  dismissLabel = 'Dismiss price change snapshot',
}) {
  const snapshotTone = snapshotToneClassNames[tone] ?? snapshotToneClassNames.pricing

  return (
    <section
      aria-label={title}
      data-tone={tone}
      data-testid="price-change-snapshot"
      className={cn(
        'relative overflow-hidden rounded-xl border transition-[border-color,background,box-shadow]',
        snapshotTone
      )}
    >
      {onDismiss ? (
        <Button
          variant="ghostMuted"
          size="icon-xs"
          className="absolute top-3 right-3 z-10"
          aria-label={dismissLabel}
          onClick={onDismiss}
        >
          <X className="size-4" />
        </Button>
      ) : null}

      <div className="relative p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className={cn('flex min-w-0 items-center gap-2.5', onDismiss && 'pr-10')}>
            <BadgeCheck className="size-4 shrink-0 text-info" aria-hidden="true" />
            <Typography variant="cardTitle">{title}</Typography>
          </div>

          {action ? (
            <div className={cn('flex flex-wrap items-center gap-2', onDismiss && 'sm:mr-10')}>
              {action}
            </div>
          ) : null}
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <PriceChangeSnapshotPanel
            title="Affecting prices right now..."
            tone={currentTone}
            bodyAction={currentAction}
          >
            {currentBody}
          </PriceChangeSnapshotPanel>
          <PriceChangeSnapshotPanel title="Coming up next..." tone={nextTone}>
            {nextBody}
          </PriceChangeSnapshotPanel>
        </div>
      </div>
    </section>
  )
}
